import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { label } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Card, Empty } from "../common/UI";
import { StatusBadge } from "../common/Badges";
import { Button } from "../ui/button";

const TYPES = ["amends", "partially_amends", "repeals", "partially_repeals", "supersedes", "implements", "supplements", "references", "related_to"];

function AddRelation({ m, onSaved }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);
  const [type, setType] = useState("amends");
  const [notes, setNotes] = useState("");
  const search = () => api.get("/measures", { params: { q, page_size: 8 } }).then((r) => setResults(r.data.items.filter((x) => x.id !== m.id)));
  const save = async () => {
    try {
      await api.post(`/measures/${m.id}/relationships`, { target_measure_id: target.id, relation_type: type, notes: notes || null });
      toast.success("Relationship recorded");
      setTarget(null);
      setResults([]);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Record a relationship" testId="add-relationship">
      <div className="flex gap-2">
        <input className="otms-input" placeholder="Find the related measure by number, reference or title" value={q} onChange={(e) => setQ(e.target.value)} data-testid="relationship-search-input" />
        <Button size="sm" variant="outline" className="h-9" onClick={search} data-testid="relationship-search-button">Find</Button>
      </div>
      {results.length > 0 && !target && (
        <ul className="mt-2 divide-y rounded border">
          {results.map((r) => (
            <li key={r.id}><button className="w-full px-3 py-2 text-left text-xs hover:bg-sky-50" onClick={() => setTarget(r)} data-testid={`relationship-pick-${r.id}`}>{r.official_number || r.tracking_ref} — {r.proposed_title}</button></li>
          ))}
        </ul>
      )}
      {target && (
        <div className="mt-3 grid gap-2 md:grid-cols-[1fr_200px]">
          <div className="text-xs">This measure <b>{label(type).toLowerCase()}</b> <b>{target.official_number || target.tracking_ref}</b></div>
          <select className="otms-input" value={type} onChange={(e) => setType(e.target.value)} data-testid="relationship-type-select">{TYPES.map((t) => <option key={t} value={t}>{label(t)}</option>)}</select>
          <input className="otms-input md:col-span-2" placeholder="Notes (e.g., sections affected)" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <div className="flex justify-end gap-2 md:col-span-2"><Button size="sm" variant="outline" onClick={() => setTarget(null)}>Cancel</Button><Button size="sm" className="bg-sky-600" onClick={save} data-testid="relationship-save-button">Save</Button></div>
        </div>
      )}
    </Card>
  );
}

export default function RelationshipsTab({ m, onSaved }) {
  const { can } = useAuth();
  return (
    <div className="space-y-4">
      <Card title="Legislative relationships" testId="relationships-list">
        <p className="mb-3 text-[11px] text-slate-500">Relationships are administrative cross-references. Original and amended texts remain separately preserved; no consolidated text is generated automatically.</p>
        {m.relationships.length === 0 && <Empty text="No related measures recorded." />}
        <ul className="divide-y divide-slate-100">
          {m.relationships.map((r) => (
            <li key={r.id} className="py-2.5 text-xs" data-testid={`relationship-${r.id}`}>
              <div className="text-slate-500">{r.direction === "outgoing" ? `This measure ${label(r.relation_type).toLowerCase()}` : `${label(r.relation_type)} this measure:`}</div>
              {r.other ? (
                <Link to={`/measures/${r.other.id}`} className="mt-0.5 flex items-center gap-2 font-medium text-slate-800 hover:text-sky-700">
                  {r.other.official_number || r.other.tracking_ref} — {r.other.proposed_title} <StatusBadge category={r.other.stage_category} name={r.other.stage_name} />
                </Link>
              ) : <span className="italic text-slate-400">Restricted record</span>}
              {r.notes && <div className="mt-0.5 text-slate-500">{r.notes}</div>}
            </li>
          ))}
        </ul>
      </Card>
      {can("measure.edit") && <AddRelation m={m} onSaved={onSaved} />}
    </div>
  );
}
